import { CountryCode } from "libphonenumber-js";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { PassengerContact } from "@/redux/slices/clientBooking/types";
import { normalisePhone } from "@/utils/normalisePhone";

export const normaliseField = (field?: string | null) => (field ?? "").trim().toLowerCase();
export const normaliseTitle = (title?: string | null) => normaliseField(title).replace(/\.$/, "");

export interface PassengerChange {
  changedLineName: string;
  tooltipLabel: string;
  saved: string;
  booked: string;
}

// Bookings can be stored without a title, and an account with no mobile is booked with whatever number
// the backend falls back to, so a missing title or number is no change.
const titlesDiffer = (a: PassengerContact, b: PassengerContact) =>
  !!normaliseTitle(a.title) && !!normaliseTitle(b.title) && normaliseTitle(a.title) !== normaliseTitle(b.title);

const nameShowingTitleIfItDiffers = (c: PassengerContact, other: PassengerContact) =>
  [titlesDiffer(c, other) ? c.title : "", c.firstName, c.lastName].filter((part) => part?.trim()).join(" ");

const phoneDigits = (c: PassengerContact, country?: CountryCode) => normalisePhone(c.phone ?? "", country).replace(/[^\d+]/g, "");

const orNotSet = (value: string) => value || "Not set";

const FIELDS: {
  changedLineName: string;
  tooltipLabel: string;
  differs: (saved: PassengerContact, booked: PassengerContact, country?: CountryCode) => boolean;
  display: (c: PassengerContact, other: PassengerContact, country?: CountryCode) => string;
}[] = [
  {
    changedLineName: "name",
    tooltipLabel: "Name",
    differs: (a, b) =>
      titlesDiffer(a, b) ||
      normaliseField(a.firstName) !== normaliseField(b.firstName) ||
      normaliseField(a.lastName) !== normaliseField(b.lastName),
    display: nameShowingTitleIfItDiffers,
  },
  {
    changedLineName: "email",
    tooltipLabel: "Email",
    differs: (a, b) => normaliseField(a.email) !== normaliseField(b.email),
    display: (c) => c.email?.trim() ?? "",
  },
  {
    changedLineName: "contact number",
    tooltipLabel: "Contact number",
    differs: (a, b, country) => {
      const [saved, booked] = [phoneDigits(a, country), phoneDigits(b, country)];
      return !!saved && !!booked && saved !== booked;
    },
    display: (c, _other, country) => {
      const phone = normalisePhone(c.phone ?? "", country);
      return formatPhoneNumberIntl(phone) || phone;
    },
  },
];

export const passengerChanges = (saved: PassengerContact, booked: PassengerContact, country?: CountryCode): PassengerChange[] =>
  FIELDS.filter(({ differs }) => differs(saved, booked, country)).map(({ changedLineName, tooltipLabel, display }) => ({
    changedLineName,
    tooltipLabel,
    saved: orNotSet(display(saved, booked, country)),
    booked: orNotSet(display(booked, saved, country)),
  }));
